import { SessionContext } from "@/context/SessionContext";
import ConductionAPI, { BalancingResult } from "@/javascript/conduction-api";
import { Classroom, ProjectSubmissionToken, User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Group,
    Modal,
    ScrollArea,
    Skeleton,
    Stack,
    Table,
    Text,
    Title,
    Tooltip
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconCircleCheck, IconExclamationCircle, IconFilePlus, IconInfoSquare, IconScale, IconTrash } from "@tabler/icons-react";
import { useContext, useMemo, useState } from "react";
import SubmissionTokenCreatorModal from "./SubmissionTokenCreatorModal";

interface SubmissionTokensBrowserModalProps {
    showModal: boolean;
    setShowModal: (show: boolean) => void;
    classroom?: Classroom | null;
    user?: User | null;
    onChange?: () => void;
}

const SubmissionTokensBrowserModal: React.FC<SubmissionTokensBrowserModalProps> = ({ classroom, user, showModal, setShowModal, onChange }) => {
    const [showCreateTokenModal, setShowCreateTokenModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [balancingResults, setBalancingResults] = useState<BalancingResult[] | null>(null);

    const session = useContext(SessionContext);
    if (session === undefined) {
        throw new Error("SubmissionTokensBrowserModal must be a child of SessionContext.");
    }
    const token = session.token;
    if (token === null) {
        return;
    }

    const deleteToken = (tokenId: string) => {
        let id: string | undefined;
        let type: "classroom" | "user" | undefined;

        if (classroom == undefined && user != undefined) {
            id = user.id;
            type = "user";
        }

        if (classroom != undefined && user == undefined) {
            id = classroom.id;
            type = "classroom";
        }

        if (id === undefined || type === undefined) {
            return;
        }

        setLoading(true);
        const notificationId = Utils.displayNotification(
            "Deleting Token",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.removeSubmissionToken(type, id, tokenId, token)
            .then(() => {
                if (onChange) {
                    onChange();
                }
                Utils.updateNotification(
                    notificationId,
                    "Token Deleted",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Occurred",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            }).finally(() => setLoading(false));
    }

    const handleBalanceSubmissionCredits = () => {
        let id: string | undefined;
        let type: "classroom" | "user" | undefined;

        if (classroom == undefined && user != undefined) {
            id = user.id;
            type = "user";
        }

        if (classroom != undefined && user == undefined) {
            id = classroom.id;
            type = "classroom";
        }

        if (id === undefined || type === undefined) {
            return;
        }

        setLoading(true);
        const notificationId = Utils.displayNotification(
            "Attempting Balance...",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.balanceSubmissionTokens(type, id, token)
            .then((results: BalancingResult[]) => {
                setBalancingResults(results);
                if (onChange) {
                    onChange();
                }
                Utils.updateNotification(
                    notificationId,
                    "Balancing Complete!",
                    "See the results below.",
                    "blue",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "An Error Occurred",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            }).finally(() => setLoading(false));
    }

    const tokens = useMemo(() => user != undefined ? user.tokens : classroom?.tokens, [user, classroom]);
    const activeTokens = useMemo(() => tokens?.filter((currentToken) => currentToken.isActive()) ?? [], [tokens]);
    const totalActiveCredits = useMemo(() => activeTokens.reduce((acc: number, token: ProjectSubmissionToken) => acc + token.credits, 0), [activeTokens]);
    const usedActiveCredits = useMemo(() => activeTokens.reduce((acc: number, token: ProjectSubmissionToken) => acc + token.submissions.length, 0), [activeTokens]);
    const remainingActiveCredits = useMemo(() => totalActiveCredits - usedActiveCredits, [totalActiveCredits, usedActiveCredits]);

    if (user === null || classroom === null) {
        // null indicates that we know which type this is for, but we do not have a target user/classroom yet
        return null; // Nothing to show
    }

	if (user === undefined && classroom === undefined) {
		// Neither user nor classroom are defined
		throw new Error("User or classroom must be provided.");
	}

	if (user !== undefined && classroom !== undefined) {
		// Both user and classroom are defined
		throw new Error("User and classroom cannot both be provided.")
	}

    return (
        <Modal opened={showModal} onClose={() => setShowModal(false)} size="80vw" title={`Submission Tokens`}>
            <SubmissionTokenCreatorModal
                classroom={classroom}
                user={user}
                showModal={showCreateTokenModal}
                setShowModal={setShowCreateTokenModal}
                onCreate={onChange}
            />

            <Modal
                opened={balancingResults != null}
                onClose={() => {
                    setBalancingResults(null);
                    setLoading(false);
                }}
                title="Balancing Results"
                size="80vw"
                styles={{
                    title: {
                        fontSize: 26,
                        fontVariant: "all-small-caps"
                    }
                }}
            >
                <Stack>
                    {(balancingResults == null || balancingResults.length <= 0) ? (
                        <Text>
                            No results.
                        </Text>
                    ) : (
                        <Stack>
                            {balancingResults.map((balancingResult) => (
                                <Group>
                                    {(balancingResult.success === null) ?
                                        <IconCircleCheck size={24} color="blue" />
                                        : (balancingResult.success === true) ?
                                            <IconCircleCheck size={24} color="green" />
                                            :
                                            <IconExclamationCircle size={24} color="red" />
                                    }
                                    <Text>
                                        {balancingResult.message}
                                    </Text>
                                </Group>
                            ))}
                        </Stack>
                    )}
                    {balancingResults !== null && balancingResults.some(br => br.success === false) &&
                        <Group>
                            <IconInfoSquare size={24} color="blue" />
                            <Text>
                                Consider adding credits that fit the time frames above to remedy the discrepency.
                            </Text>
                        </Group>
                    }
                </Stack>
            </Modal>

            <Group justify="space-between">
                <Title>{user !== undefined ? `${user.username}'s personal account` : `${classroom!.name} classroom`}</Title>
                <Group>
                    <Button
                        onClick={() => setShowCreateTokenModal(true)}
                        leftSection={<IconFilePlus />}
                    >
                        Add Submission Credits
                    </Button>
                    <Tooltip
                        withArrow
                        label="Attempt to associate orphaned submissions with these tokens' credits"
                    >
                        <Button
                            onClick={handleBalanceSubmissionCredits}
                            disabled={tokens == null || tokens.length === 0}
                            leftSection={<IconScale />}
                        >
                            Balance Credits
                        </Button>
                    </Tooltip>
                </Group>
            </Group>

            <ScrollArea
                style={{
                    width: "100%"
                }}
            >
                {loading ? (
                    <Skeleton height={"100%"} radius="md" />
                ) : tokens == null || tokens.length === 0 ? (
                    <Text>No tokens found.</Text>
                ) : (
                    <Table
                        striped
                        stickyHeader
                        highlightOnHover
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Credits Available</Table.Th>
                                <Table.Th>Used Credits</Table.Th>
                                <Table.Th>Created Date</Table.Th>
                                <Table.Th>Active Start Date</Table.Th>
                                <Table.Th>Expiration Date</Table.Th>
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {tokens.map((submissionToken) => {
                                return (
                                    <Table.Tr key={submissionToken.id}>
                                        <Table.Td>{submissionToken.credits}</Table.Td>
                                        <Table.Td><Badge color={submissionToken.submissions.length >= submissionToken.credits ? "red" : "blue"}>{submissionToken.submissions.length}</Badge></Table.Td>
                                        <Table.Td><Badge color="blue">{formatDateString(submissionToken.dateCreated)}</Badge></Table.Td>
                                        <Table.Td><Badge color={submissionToken.passedStartDate() ? "green" : "yellow"}>{formatDateString(submissionToken.startDate)}</Badge></Table.Td>
                                        <Table.Td><Badge color={submissionToken.isExpired() ? "red" : "green"}>{formatDateString(submissionToken.expirationDate)}</Badge></Table.Td>
                                        <Table.Td>
                                            <Tooltip
                                                label={"Delete"}
                                                withArrow
                                            >
                                                <Button
                                                    size="xs"
                                                    onClick={async () => deleteToken(submissionToken.id)}
                                                >
                                                    <IconTrash size={20} />
                                                </Button>
                                            </Tooltip>
                                        </Table.Td>
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                )}
            </ScrollArea>

            <Table
                striped
                stickyHeader
                highlightOnHover
            >
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Total Available Active Credits</Table.Th>
                        <Table.Th>Total Used Active Credits</Table.Th>
                        <Table.Th>Total Remaining Active Credits</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    <Table.Tr>
                        <Table.Td><Badge color={totalActiveCredits > 0 ? "green" : "red"}>{totalActiveCredits}</Badge></Table.Td>
                        <Table.Td><Badge color={usedActiveCredits >= totalActiveCredits ? "red" : "green"}>{usedActiveCredits}</Badge></Table.Td>
                        <Table.Td><Badge color={remainingActiveCredits > 0 ? "green" : "red"}>{remainingActiveCredits}</Badge></Table.Td>
                    </Table.Tr>
                </Table.Tbody>
            </Table>
        </Modal>
    );
};

function formatDateString(stringDate: Date): string {
    if (!stringDate) return "Invalid Date";
    const date = new Date(stringDate + " UTC");
    return date.toLocaleDateString();
}

export default SubmissionTokensBrowserModal;
